<template>
  <div class="account-order">
    <div class="account-order__inner">
      <div class="account-order__cost">
        <div
          v-if="!!discountPercent"
          class="account-order__discount">
          <div
            :key="discountPercent"
            class="discount__value">
            {{ discountPercent }}% <br> OFF
          </div>
        </div>

        <div
          v-if="showOldPrice"
          :key="currencyValue(price.oldPrice)"
          class="cost__old">
          {{ currencyValue(price.oldPrice) }}
        </div>

        <div class="cost__new">
          <upper-cents-price
            :key="price.newPrice + 'cents'"
            :sum="price.newPrice || 0"
            class="account-order__price" />
        </div>
      </div>

      <CashbackBadge
        :price="price.newPrice"
        :type="'product-account'"
        class="account-order__cashback">
        cashback
        <info-tooltip text="Will be added to your Skycoach balance" />
      </CashbackBadge>

      <ui-input
        v-if="!user && !unavailableText"
        v-model.trim="email"
        :error="errors.email"
        name="email"
        inputmode="email"
        autocomplete="off"
        class="account-order__input"
        placeholder="Your email"
        :z-index-tooltip="2147483011"
        @input="errors.email = ''" />

      <cashback-switcher
        v-if="product.is_enabled_cashback_writeoff"
        :price="price.newPrice"
        :old-price="price.oldPrice"
        type="account"
        :is-payment-method-opened="isOpenPaymentMethodSelector"
        class="account-order__cashback-switcher"
        @cashback-applied="handleCashbackApplied" />

      <div class="account-order__buttons">
        <button
          v-if="userIsBooster"
          class="button button--disabled account-order__buy-now">
          Pro can't buy
        </button>

        <button
          v-else-if="unavailableText"
          class="button button--disabled account-order__buy-now">
          {{ unavailableText }}
        </button>

        <div
          v-else
          class="button button-main account-order__buy-now"
          @click.stop="openPaymentMethodSelector">
          Buy Account

          <img
            data-src="/images/icons/arrow-left.svg"
            class="lazyload account-order__buy-now--img"
            width="18"
            height="11"
            alt="">
        </div>

        <div
          v-if="isStripeButtonVisible"
          class="button account-order__buy-gpay transition--color-bg"
          @click.stop="buyWithStripe">
          <img
            alt=""
            width="51"
            height="21"
            src="/images/payment_systems/google-pay.svg"
            class="gpay-button__img">
        </div>
      </div>

      <div class="account-order__payment-systems">
        <img
          v-for="item in paymentMethods"
          :key="item.id"
          class="payment-item"
          height="14"
          width="37"
          alt=""
          :src="`/images/payment_systems/white/${item.pic}`">
      </div>
    </div>

    <div class="account-order__chat">
      <div class="chat__info">
        <p class="any-questions">
          Any questions?
        </p>

        <p class="before-order">
          Chat before ordering
        </p>
      </div>

      <div
        class="chat__button button button-basic-blue-dark"
        @click="openIntercom">
        <p class="chat__button-text">
          {{ isMobile ? "Chat" : "Open chat" }}
        </p>

        <img
          data-src="/images/icons/chat2.svg"
          width="24"
          height="24"
          alt=""
          class="lazyload chat__button-img">
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import UserModel from 'Models/UserModel';
import PaymentSystemModel from 'Models/PaymentSystemModel';
import IntercomApiDriver from 'Root/drivers/IntercomApiDriver';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import UpperCentsPrice from 'Components/ui/Price/UpperCentsPrice.vue';
import UiInput from 'Components/ui/Input/UiInput.vue';
import PaymentMethodSelector from 'Components/PaymentPopups/PaymentMethodSelector.vue';
import CashbackBadge from 'Components/CashbackBadge.vue';
import CashbackSwitcher from 'Components/CashbackSwitcher.vue';
import { regExpEmail } from 'Root/helpers/regexps';
import fingerprintHeader from 'Root/mixins/fingerprintHeader';
import orderHelperMixin from 'Root/mixins/orderHelperMixin';
import { canSupportApplePay } from 'Root/helpers/tools';
import { getPriceWithCommission } from 'Root/helpers/product';
import RegionModel from 'Models/RegionModel';
import GameModel from 'Models/GameModel';
import criteoAnalytics from 'Root/drivers/criteoAnalytics';
import AppModel from 'Models/AppModel';
import InfoTooltip from 'Components/ui/Tooltip/InfoTooltip.vue';

const { API_URI } = AppModel;

export default {
  name: 'AccountOrderArea',
  components: {
    CashbackSwitcher,
    CashbackBadge,
    UiInput,
    UpperCentsPrice,
    InfoTooltip,
  },

  mixins: [fingerprintHeader, orderHelperMixin],

  props: {
    offerData: {
      type: Object,
      required: true,
    },

    product: {
      type: Object,
      required: true,
    },

    currentPrice: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      selectedPaymentMethod: null,
      email: '',
      errors: {
        email: '',
      },
      runsCount: 1,

      price: {
        newPrice: this.currentPrice,
        oldPrice: this.currentPrice,
      },
      usedCashback: null,
      isOpenPaymentMethodSelector: false,

      availableStripePaymentMethods: {
        googlePay: false,
      },
    };
  },

  computed: {
    ...mapState(['user', 'activeRegion', 'userDevice', 'usdToCurrencyRatio', 'countryCode']),
    ...mapState('products', ['accountOffer']),
    ...mapGetters(['calcPriceFromUsd', 'isUsersFirstOrder', 'currencyValue']),

    userIsBooster() {
      return [UserModel.ROLES.PRO.id, UserModel.ROLES.PRO_CANDIDATE.id].includes(+this.user?.role);
    },

    userEmail() {
      return this.user?.email || this.email;
    },

    isMobile() {
      return this.userDevice.innerWidth < 640;
    },

    unavailableText() {
      return this.product.meta?.unavailableText;
    },

    showOldPrice() {
      return this.price.newPrice < this.price.oldPrice;
    },

    discountPercent() {
      const { newPrice, oldPrice } = this.price;
      const result = Math.max(Math.round(((oldPrice - newPrice) / oldPrice) * 100), 0);
      return Number.isNaN(result) ? 0 : Math.round(result);
    },

    paymentMethods() {
      return PaymentSystemModel.METHODS_LIST.filter(
        (method, index, list) => list.findIndex((item) => item.title === method.title) === index,
      );
    },

    isStripeButtonVisible() {
      const stripeAvailable = this.availableStripePaymentMethods.googlePay;
      const userCanUseStripe = PaymentSystemModel.METHODS.GOOGLE_PAY.onlyForUsersWithId
        ? PaymentSystemModel.METHODS.GOOGLE_PAY.onlyForUsersWithId.includes(this.user?.id) : true;
      return stripeAvailable && userCanUseStripe;
    },
  },

  watch: {
    currentPrice() {
      this.price.newPrice = this.currentPrice;
      this.price.oldPrice = this.currentPrice;
    },

    selectedPaymentMethod() {
      this.calcPrice();
    },
  },

  mounted() {
    if (window.Stripe) {
      this.checkStripeAvailableMethods();
    } else {
      const stripeScript = document.getElementById('stripe-script');
      if (!stripeScript) {
        return;
      }

      stripeScript.onload = () => {
        this.checkStripeAvailableMethods();
      };
    }
  },

  methods: {
    validateEmail() {
      if (!this.email.match(regExpEmail)) {
        this.errors.email = 'Please, enter a valid email';
        googleTagAnalytics.fireError('buy_now_1', 'wrong_email');
        return false;
      }

      return true;
    },

    sendGooglePaymentAnalytics() {
      const product = {
        ...this.product,
        title: this.offerData?.outer_name ?? this.product.title,
      };

      const game = GameModel.ID_TO_DATA[this.product.game].fullTitle;
      const currency = RegionModel.ID_TO_DATA[this.$store.state.activeRegion.id].currency.title;
      const runs = this.runsCount;

      googleTagAnalytics.emitOpenPaymentNew(game, product, this.price.newPrice, currency, runs);

      const tagId = this.product?.master_tag_id || this.product.tags[0].id;
      const orderTag = this.product.tags.find((tag) => tag.id === tagId);

      googleTagAnalytics.firePaymentPopup(product, orderTag.title, this.price.newPrice);

      criteoAnalytics.fireAddToCartEvent(
        this.user?.email || this.userEmail,
        this.product?.id,
        this.price?.newPrice,
        runs,
      );
    },

    openPaymentMethodSelector() {
      const isEmailValid = this.user || (!this.user && this.validateEmail());

      if (!isEmailValid) {
        return;
      }

      this.isOpenPaymentMethodSelector = true;

      this.$vuedals.openMobilePanel({
        class: this.selfplayOption && !this.selfplayOption.isChecked ? 'vuedals__payment-selector' : null,
        title: 'Choose payment method',
        component: PaymentMethodSelector,
        data: {
          component: this,
          offerData: this.offerData,
        },
        onClose: ({ methodId, wmNumber }) => {
          this.selectedPaymentMethod = methodId;
          this.calcPrice();
          this.order(this.userEmail, methodId, wmNumber);
        },
        onDismiss: () => {
          this.paymentMethodSelectorOpened = false;
          this.selectedPaymentMethod = null;
        },
      });

      this.sendGooglePaymentAnalytics();
    },

    calcPrice() {
      const basePrice = this.currentPrice;

      this.price.newPrice = getPriceWithCommission(basePrice, this.selectedPaymentMethod, {
        regionId: this.activeRegion.id,
        isFirstPurchase: this.isUsersFirstOrder,
      });

      if (this.usedCashback && this.usedCashback.isApplied) {
        const cashbackValue = this.usedCashback.value;
        this.price.newPrice = Math.max(0, this.price.newPrice - cashbackValue);
      }
    },

    openIntercom() {
      googleTagAnalytics.emitItemOpenChatEvent();
      IntercomApiDriver.show();
    },

    async buyWithStripe() {
      if (!this.validateEmail()) {
        return;
      }

      this.selectedPaymentMethod = PaymentSystemModel.METHODS.GOOGLE_PAY.id;

      this.sendGooglePaymentAnalytics();

      const response = await this.order(this.email, PaymentSystemModel.METHODS.GOOGLE_PAY.id, null, false);
      if (response) {
        const [redirectUrl, clientSecret] = response.url && response.url.includes('&client_secret=')
          ? response.url.split('&client_secret=') : [response.url, null];
        const currency = this.activeRegion.currency.title.toLowerCase();

        this.stripePaymentRequest.update({
          currency,
          total: {
            label: this.product?.title,
            amount: this.parsePriceToStripeAmount(this.price.newPrice.toFixed(2)),
          },
        });

        if (clientSecret && redirectUrl) {
          this.listenStripeRequest(redirectUrl, clientSecret);
          this.stripePaymentRequest.show();
        }
      }
    },

    listenStripeRequest(redirectUrl, clientSecret) {
      this.stripePaymentRequest.on('paymentmethod', (event) => {
        console.log('stripePaymentRequest.on: PAYMENT METHOD', event);
        this.$axios.post(`${API_URI}/log/add`, {
          message: `${this.product.id} | Start`,
        });
        // Confirm the PaymentIntent without handling potential next actions (yet).
        this.stripe.confirmCardPayment(
          clientSecret,
          { payment_method: event.paymentMethod.id },
          { handleActions: false },
        ).then((confirmResult) => {
          if (confirmResult.error) {
            // Report to the browser that the payment failed, prompting it to
            // re-show the payment interface, or show an error message and close
            // the payment interface.
            event.complete('fail');
            console.log('FAIL | PAYMENT EVENT COMPLETED');
            this.$axios.post(`${API_URI}/log/add`, {
              message: `${this.product.id} | Payment failed. Payment method: ${event.paymentMethod.id}`,
            });
            this.enableInterface();
          } else {
            // Report to the browser that the confirmation was successful, prompting
            // it to close the browser payment method collection interface.
            event.complete('success');
            console.log('SUCCESS | PAYMENT EVENT COMPLETED');
            this.$axios.post(`${API_URI}/log/add`, {
              message: `${this.product.id} | Payment event completed.
                PaymentIntent status: ${confirmResult.paymentIntent.status}.
                PaymentIntent next_actions: ${confirmResult.paymentIntent.next_actions}`,
            });
            // Check if the PaymentIntent requires any actions and if so let Stripe.js
            // handle the flow. If using an API version older than "2019-02-11" instead
            // instead check for: `paymentIntent.status === "requires_source_action"`.
            if (confirmResult.paymentIntent.status === 'requires_action') {
              // Let Stripe.js handle the rest of the payment flow.
              this.stripe.confirmCardPayment(clientSecret).then((result) => {
                this.$axios.post(`${API_URI}/log/add`, { message: `${this.product.id} | Confirm card payment` });
                if (result.error) {
                  this.$axios.post(`${API_URI}/log/add`, {
                    message: `${this.product.id} | Confirm card payment failed. Error: ${result.error}`,
                  });
                  alert('Failed to create payment. Please try another payment method.');
                  // The payment failed -- ask your customer for a new payment method.
                } else {
                  this.$axios.post(`${API_URI}/log/add`, {
                    message: `${this.product.id} | Confirm card payment Success`,
                  });
                  document.location.href = redirectUrl;
                  // The payment has succeeded.
                }
              });
            } else {
              this.$axios.post(`${API_URI}/log/add`, {
                message: `${this.product.id} | Confirm card payment Success`,
              });
              document.location.href = redirectUrl;
              // The payment has succeeded.
            }
          }
        });
      });

      this.stripePaymentRequest.on('token', (event) => {
        console.log('stripePaymentRequest.on: TOKEN', event);
      });

      this.stripePaymentRequest.on('source', (event) => {
        console.log('stripePaymentRequest.on: SOURCE', event);
      });

      this.stripePaymentRequest.on('cancel', () => {
        console.log('stripePaymentRequest.on: CANCEL');
        this.enableInterface();
      });
    },

    parsePriceToStripeAmount(price) {
      let amount = 0;

      if (String(price)
        .includes('.')) {
        const splittedPrice = String(price)
          .split('.');
        amount = (Number(splittedPrice[0]) * 100) + Number(splittedPrice[1]);
      } else {
        amount = price * 100;
      }

      return +amount;
    },

    async checkStripeAvailableMethods() {
      this.stripe = window.Stripe(PaymentSystemModel.STRIPE_PUBLIC_KEY);

      const country = this.countryCode;
      const currency = this.activeRegion.currency.title.toLowerCase();

      if (!country) {
        return;
      }

      this.stripePaymentRequest = this.stripe.paymentRequest({
        country,
        currency,
        total: {
          label: this.product?.title,
          amount: this.parsePriceToStripeAmount(this.price.newPrice.toFixed(2)),
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      let canMakePaymentVia = null;

      try {
        canMakePaymentVia = await this.stripePaymentRequest.canMakePayment();
        // eslint-disable-next-line no-empty
      } catch (e) {}

      if (canMakePaymentVia) {
        if (Object.keys(canMakePaymentVia)
          .includes('googlePay') && !canSupportApplePay()) {
          this.availableStripePaymentMethods.googlePay = true;
        }
      }
    },

    handleCashbackApplied(cashback) {
      this.usedCashback = {
        ...cashback,
        basePrice: this.currentPrice,
      };
      this.calcPrice();
    },
  },
};
</script>

<style scoped lang="scss">
.account-order {
  display: flex;
  flex-direction: column;
}

.account-order__inner {
  position: relative;
  display: grid;
  grid-auto-flow: row;
  grid-row-gap: 16px;
  padding: 24px;
  border: 1px solid $color--basic-menu;
  border-radius: 16px;
  background-color: rgba($color--basic-black, 0.8);

  .cost__new-value {
    --cent-margin: -5px 0 0 2px;
  }
}

.account-order__cashback-switcher {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.account-order__price {
  font-size: 35px;
  justify-self: center;
}

.account-order__warranty {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.account-order__warranty-toggle {
  display: flex;
  align-items: center;
}

.account-order__warranty-text {
  font-size: 15px;
  font-weight: 400;
}

.account-order__warranty-price {
  font-size: 15px;
  font-weight: 500;
  margin-right: 16px;
}

/deep/.account-order__input .ui-input__wrap-inner {
  background-color: rgba($color--basic-blue, 0.25);
  border: 2px solid rgba($color--basic-blue, 0.25);
  height: 48px;
}

/deep/.ui-input__input {
  font-size: 15px;
  color: $color--basic-white;
}

.account-order__buttons {
  display: grid;
  grid-auto-flow: row;
  grid-row-gap: 16px;
}

.account-order__buy-gpay {
  padding: 12px 0;
  background-color: $color--basic-menu;
}

.account-order__buy-now {
  display: flex;
  justify-content: center;
  font-size: 15px;
  font-weight: 500;
  padding: 12px 0;
}

.account-order__buy-now--img {
  transform: rotate(180deg);
  align-self: center;
  margin-left: 11px;
}

.account-order__payment-systems {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
}

.payment-item {
  filter: $filter--white-to-dark-gray;
  height: 14px;
  width: auto;
  margin: 0 4px 4px;
}

.account-order__chat {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
}

.chat__info {
  display: flex;
  flex-direction: column;
  font-size: 13px;
  font-weight: 400;
}

.chat__button {
  display: flex;
  align-items: center;
  padding: 12px 30px;
  border-radius: 100px;
}

.chat__button-text {
  font-size: 15px;
  font-weight: 500;
  margin-right: 8px;
}

.account-order__discount {
  position: absolute;
  top: 0;
  right: 0;
  background: url('/images/bg/star-discount.svg') no-repeat center;
  background-size: 100%;
  width: 64px;
  height: 64px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.discount__value {
  font-weight: 500;
  font-size: 15px;
  text-align: center;
}

.account-order__cost {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: baseline;
}

.cost__old {
  grid-area: old;
  color: $color--basic-white;
  font-weight: 500;
  font-size: 15px;
  text-decoration-line: line-through;
  display: flex;
}

.cost__new {
  grid-area: new;
  font-weight: 500;
  font-size: 35px;
  line-height: 41px;
}
</style>
